import type { RepoState } from "../lib/types";
import { computeDiff, truncateMsg } from "../lib/diff";
import { I } from "./Icons";

export type PRReviewState = "approved" | "changes_requested" | "commented";

export interface PRComment {
  author: string;
  initials: string;
  color: string;
  body: string;
  at: string;
}

export interface PRReview {
  author: string;
  initials: string;
  color: string;
  state: PRReviewState;
}

export interface PRState {
  exists: boolean;
  number: number;
  title: string;
  description: string;
  comments: PRComment[];
  reviews: PRReview[];
  merged: boolean;
}

export const INITIAL_PR_STATE: PRState = {
  exists: false,
  number: 42,
  title: "Refresh homepage copy",
  description:
    "Updates the homepage heading and tagline to match the latest brand copy. Tested locally — renders fine on all breakpoints.",
  comments: [],
  reviews: [],
  merged: false,
};

interface Props {
  repo: RepoState;
  prState: PRState;
  stepId: string;
  /** Original App.jsx content (pre-PR) — used for the diff. */
  originalApp: string;
  /** Has the learner pushed a feature branch yet? */
  featurePushed: boolean;
  onCreate: () => void;
  onComment: () => void;
  onApprove: () => void;
  onMerge: () => void;
}

const REVIEWER_JAMIE = {
  author: "jamie-tan",
  initials: "JT",
  color: "#5461AD",
};

const REVIEWER_SAM = {
  author: "sam-rao",
  initials: "SR",
  color: "#64479D",
};

export function PRPanel({
  repo,
  prState,
  stepId,
  originalApp,
  featurePushed,
  onCreate,
  onComment,
  onApprove,
  onMerge,
}: Props) {
  // Compute diff against the learner's current App.jsx
  const currentApp = repo.files["App.jsx"]?.committed ?? originalApp;
  const diffRows = computeDiff(originalApp, currentApp);
  const adds = diffRows.filter((r) => r.kind === "add").length;
  const dels = diffRows.filter((r) => r.kind === "del").length;

  // Determine PR state label
  const approved = prState.reviews.some((r) => r.state === "approved");
  let stateLabel: { text: string; color: string; bg: string; icon: keyof typeof I };
  if (prState.merged) {
    stateLabel = { text: "Merged", color: "#fff", bg: "#8250df", icon: "GitMerge" };
  } else if (prState.exists) {
    stateLabel = { text: "Open", color: "#fff", bg: "#1f883d", icon: "Branch" };
  } else {
    stateLabel = { text: "Not opened", color: "#fff", bg: "#59636e", icon: "Branch" };
  }

  return (
    <div className="gf-step-content" style={{ display: "block" }}>
      {/* Pre-PR creation form */}
      {!prState.exists && stepId === "open-pr" && (
        <PRCreateForm
          title={prState.title}
          description={prState.description}
          adds={adds}
          dels={dels}
          diffRows={diffRows}
          featurePushed={featurePushed}
          onCreate={onCreate}
        />
      )}

      {/* PR exists — full panel */}
      {prState.exists && (
        <div className="gf-gh">
          <div className="gf-gh__head">
            <h2 className="gf-gh__title">
              {prState.title} <b>#{prState.number}</b>
            </h2>
            <div className="gf-gh__meta">
              <span
                className="gf-gh__state"
                style={{ background: stateLabel.bg, color: stateLabel.color }}
              >
                {(() => {
                  const StateIcon = I[stateLabel.icon];
                  return <StateIcon size={13} />;
                })()}
                {stateLabel.text}
              </span>
              <span className="gf-gh__meta-text">
                <b>you</b>{" "}
                {prState.merged ? "merged" : "want to merge"} {repo.commits.filter((c) => c.branch === "feature").length} commit
                {repo.commits.filter((c) => c.branch === "feature").length === 1 ? "" : "s"} into{" "}
                <b>main</b> from <b>feature</b>
              </span>
            </div>
          </div>

          <div className="gf-gh__body">
            <div className="gf-gh__main">
              <div className="gf-gh__comment">
                <div className="gf-gh__comment-head">
                  <b>you</b> opened this just now
                </div>
                <div className="gf-gh__comment-body">{prState.description}</div>
              </div>

              {/* Diff */}
              <div className="gf-gh__diff">
                <div className="gf-gh__diff-head">
                  <span style={{ color: "#1f883d", fontWeight: 600 }}>App.jsx</span>
                  <span style={{ color: "#59636e", fontWeight: 400, marginLeft: 8 }}>
                    +{adds} −{dels}
                  </span>
                </div>
                {diffRows.map((row, i) => (
                  <div
                    key={i}
                    className={`gf-gh__diff-row gf-gh__diff-row--${row.kind || "ctx"}`}
                  >
                    <div className="gf-gh__diff-row-num">{row.lOld ?? ""}</div>
                    <div className="gf-gh__diff-row-num">{row.lNew ?? ""}</div>
                    <div className="gf-gh__diff-row-code">
                      {row.kind === "add" ? "+ " : row.kind === "del" ? "- " : "  "}
                      {row.text}
                    </div>
                  </div>
                ))}
              </div>

              {/* Conversation thread */}
              {prState.comments.length > 0 && (
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                  {prState.comments.map((c, i) => (
                    <ConversationItem key={i} comment={c} />
                  ))}
                </div>
              )}

              {/* Reviews summary */}
              {prState.reviews.length > 0 && (
                <div
                  style={{
                    marginTop: 14,
                    padding: "10px 14px",
                    background: "#dafbe1",
                    border: "1px solid #1a7f37",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    color: "#1a7f37",
                    fontSize: 13,
                  }}
                >
                  <I.Check size={16} />
                  <span>
                    <b>{prState.reviews.length}</b> approval
                    {prState.reviews.length === 1 ? "" : "s"} — this PR can be merged.
                  </span>
                </div>
              )}

              {/* Action bar */}
              {!prState.merged && (
                <div
                  style={{
                    marginTop: 14,
                    padding: "14px 16px",
                    background: "#f6f8fa",
                    border: "1px solid #d1d9e0",
                    borderRadius: 8,
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {stepId === "review" && (
                    <>
                      <GhBtn
                        kind="default"
                        onClick={onComment}
                        disabled={prState.comments.length >= 2}
                      >
                        <I.MessageCircle size={13} /> Leave comment
                      </GhBtn>
                      <GhBtn
                        kind="success"
                        onClick={onApprove}
                        disabled={approved}
                      >
                        <I.Check size={13} /> {approved ? "Approved" : "Approve"}
                      </GhBtn>
                    </>
                  )}
                  {stepId === "merge-pr" && (
                    <>
                      <GhBtn
                        kind="merge"
                        onClick={onMerge}
                        disabled={!approved || prState.merged}
                      >
                        <I.GitMerge size={13} />{" "}
                        {approved ? "Squash and merge" : "Approval required"}
                      </GhBtn>
                      {!approved && (
                        <span style={{ color: "#59636e", fontSize: 12 }}>
                          Approve the PR in the previous step first.
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}

              {prState.merged && (
                <div
                  style={{
                    marginTop: 14,
                    padding: "14px 16px",
                    background: "#fbefff",
                    border: "1px solid #8250df",
                    borderRadius: 8,
                    color: "#6639ba",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <I.GitMerge size={15} />
                  <span>
                    <b>Pull request successfully merged.</b> The feature commits are now on{" "}
                    <code>origin/main</code>. Your local main is one squash-commit behind — run{" "}
                    <code>git pull</code> to catch up.
                  </span>
                </div>
              )}
            </div>

            <div className="gf-gh__side">
              <div className="gf-gh__section-title">Reviewers</div>
              <ReviewerRow
                author={REVIEWER_JAMIE.author}
                initials={REVIEWER_JAMIE.initials}
                color={REVIEWER_JAMIE.color}
                state={
                  prState.reviews.find((r) => r.author === REVIEWER_JAMIE.author)?.state
                }
              />
              <ReviewerRow
                author={REVIEWER_SAM.author}
                initials={REVIEWER_SAM.initials}
                color={REVIEWER_SAM.color}
                state={undefined}
                pending
              />

              <div className="gf-gh__section-title" style={{ marginTop: 16 }}>
                Branch
              </div>
              <div style={{ fontSize: 12, color: "#59636e" }}>
                <div style={{ marginBottom: 4 }}>
                  From: <b style={{ color: "#1f2328" }}>feature</b>
                </div>
                <div>
                  Into: <b style={{ color: "#1f2328" }}>main</b>
                </div>
              </div>

              <div className="gf-gh__section-title" style={{ marginTop: 16 }}>
                Commits in PR
              </div>
              <div
                style={{
                  fontFamily: "ui-monospace, Menlo",
                  fontSize: 11,
                  color: "#59636e",
                  lineHeight: 1.8,
                }}
              >
                {repo.commits
                  .filter((c) => c.branch === "feature")
                  .map((c) => (
                    <div key={c.id}>
                      <span style={{ color: "#0969da" }}>{c.id.slice(0, 7)}</span>{" "}
                      {truncateMsg(c.msg, 22)}
                    </div>
                  ))}
                {repo.commits.filter((c) => c.branch === "feature").length === 0 && (
                  <div style={{ fontStyle: "italic" }}>No feature commits yet</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------- create form -----------------
interface CreateFormProps {
  title: string;
  description: string;
  adds: number;
  dels: number;
  diffRows: ReturnType<typeof computeDiff>;
  featurePushed: boolean;
  onCreate: () => void;
}

function PRCreateForm({
  title,
  description,
  adds,
  dels,
  diffRows,
  featurePushed,
  onCreate,
}: CreateFormProps) {
  return (
    <div className="gf-gh">
      <div className="gf-gh__head">
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#59636e",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            marginBottom: 6,
          }}
        >
          Open a pull request
        </div>
        <h2 className="gf-gh__title" style={{ marginBottom: 4 }}>
          {title}
        </h2>
        <div className="gf-gh__meta">
          <span
            className="gf-gh__state"
            style={{ background: "#59636e", color: "#fff" }}
          >
            <I.Branch size={13} /> feature → main
          </span>
          <span className="gf-gh__meta-text">
            Comparing <b>feature</b> against <b>main</b>
          </span>
        </div>
      </div>

      <div style={{ padding: 18 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#1f2328",
            marginBottom: 6,
          }}
        >
          Description
        </div>
        <div
          style={{
            border: "1px solid #d1d9e0",
            borderRadius: 6,
            padding: 12,
            background: "#f6f8fa",
            color: "#1f2328",
            fontSize: 13,
            lineHeight: 1.55,
            marginBottom: 18,
          }}
        >
          {description}
        </div>

        {featurePushed ? (
          <div className="gf-gh__diff">
            <div className="gf-gh__diff-head">
              <span style={{ color: "#1f883d", fontWeight: 600 }}>App.jsx</span>
              <span style={{ color: "#59636e", fontWeight: 400, marginLeft: 8 }}>
                +{adds} −{dels}
              </span>
            </div>
            {diffRows.map((row, i) => (
              <div
                key={i}
                className={`gf-gh__diff-row gf-gh__diff-row--${row.kind || "ctx"}`}
              >
                <div className="gf-gh__diff-row-num">{row.lOld ?? ""}</div>
                <div className="gf-gh__diff-row-num">{row.lNew ?? ""}</div>
                <div className="gf-gh__diff-row-code">
                  {row.kind === "add" ? "+ " : row.kind === "del" ? "- " : "  "}
                  {row.text}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              border: "1px dashed #d1d9e0",
              borderRadius: 6,
              padding: 18,
              textAlign: "center",
              color: "#59636e",
              fontSize: 13,
            }}
          >
            Push your feature branch to origin first — there's nothing to compare yet.
          </div>
        )}

        <div
          style={{
            marginTop: 18,
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <GhBtn kind="success" onClick={onCreate} disabled={!featurePushed}>
            <I.GitMerge size={13} /> Create pull request
          </GhBtn>
        </div>
      </div>
    </div>
  );
}

// ----------------- subcomponents -----------------
function ReviewerRow({
  author,
  initials,
  color,
  state,
  pending,
}: {
  author: string;
  initials: string;
  color: string;
  state?: PRReviewState;
  pending?: boolean;
}) {
  let statusText = "requested";
  let statusColor = "#59636e";
  let StatusIcon: keyof typeof I | null = null;
  if (state === "approved") {
    statusText = "approved";
    statusColor = "#1a7f37";
    StatusIcon = "Check";
  } else if (state === "changes_requested") {
    statusText = "requested changes";
    statusColor = "#d1242f";
  } else if (state === "commented") {
    statusText = "commented";
    statusColor = "#59636e";
  } else if (pending) {
    statusText = "requested";
    statusColor = "#59636e";
  }

  return (
    <div className="gf-gh__avatar">
      <div className="gf-gh__avatar-img" style={{ background: color }}>
        {initials}
      </div>
      <div>
        <div style={{ color: "#1f2328" }}>{author}</div>
        <div style={{ color: statusColor, fontSize: 11 }}>
          {StatusIcon && (
            <span style={{ marginRight: 4 }}>
              <I.Check size={11} style={{ verticalAlign: -1 }} />
            </span>
          )}
          {statusText}
        </div>
      </div>
    </div>
  );
}

function ConversationItem({ comment }: { comment: PRComment }) {
  return (
    <div className="gf-gh__comment">
      <div
        className="gf-gh__comment-head"
        style={{ display: "flex", alignItems: "center", gap: 8 }}
      >
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 999,
            background: comment.color,
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {comment.initials}
        </div>
        <b>{comment.author}</b>
        <span style={{ color: "#59636e", fontWeight: 400 }}>{comment.at}</span>
      </div>
      <div className="gf-gh__comment-body">{comment.body}</div>
    </div>
  );
}

function GhBtn({
  kind,
  onClick,
  disabled,
  children,
}: {
  kind: "default" | "success" | "merge";
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const palette = {
    default: { bg: "#f6f8fa", color: "#1f2328", border: "#d1d9e0" },
    success: { bg: "#1f883d", color: "#fff", border: "#1a7f37" },
    merge: { bg: "#8250df", color: "#fff", border: "#6639ba" },
  }[kind];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "7px 14px",
        background: disabled ? "#e6e8ea" : palette.bg,
        color: disabled ? "#8c959f" : palette.color,
        border: `1px solid ${disabled ? "#d1d9e0" : palette.border}`,
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {children}
    </button>
  );
}

// ----------------- helpers -----------------
export const TEAMMATE_COMMENT: PRComment = {
  author: REVIEWER_JAMIE.author,
  initials: REVIEWER_JAMIE.initials,
  color: REVIEWER_JAMIE.color,
  body:
    "Looks good — nice copy update. One nit: maybe \"operating system\" → \"workspace\" feels more in line with how marketing describes it elsewhere. Non-blocking, just a thought.",
  at: "1 minute ago",
};

export const YOUR_REPLY: PRComment = {
  author: "you",
  initials: "YO",
  color: "#2D9BD6",
  body:
    "Good call — I'll keep the existing wording for this PR and open a follow-up to align with the marketing copy across the homepage.",
  at: "just now",
};

export const JAMIE_APPROVAL: PRReview = {
  author: REVIEWER_JAMIE.author,
  initials: REVIEWER_JAMIE.initials,
  color: REVIEWER_JAMIE.color,
  state: "approved",
};
